import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from './AuthContext';
import api from '../api';
import {
  buildCommissionSummary,
  DEFAULT_COMMISSION_RATE,
  getCategoryKey,
  getCommissionAmountForCourse,
  getCommissionSettings,
  saveCommissionSettings
} from '../utilities/commissions';

const adminTabs = [
  { id: 'overview', label: 'Resumen' },
  { id: 'users', label: 'Usuarios' },
  { id: 'courses', label: 'Cursos' },
  { id: 'finance', label: 'Finanzas' }
];

const formatDate = (value) => {
  if (!value) return 'Sin fecha';
  return new Date(value).toLocaleDateString('es-BO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatCurrency = (value) => `Bs ${Number(value || 0).toFixed(2)}`;

const AdminDashboard = ({ onNavigate, onLogout }) => {
  const { currentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [courseSearch, setCourseSearch] = useState('');
  const [courseStatusFilter, setCourseStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [commissionSettings, setCommissionSettings] = useState(getCommissionSettings());

  const loadDashboard = async () => {
    const response = await api.get('/users/admin/overview');
    return response.data?.success ? response.data.data : response.data;
  };

  const loadUsers = async () => {
    const response = await api.get('/users/admin/users');
    return response.data?.success ? response.data.data : response.data;
  };

  const loadCourses = async () => {
    const response = await api.get('/courses/admin/all');
    return response.data?.success ? response.data.data : response.data;
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      setError('');
      const [dashboardData, usersData, coursesData] = await Promise.all([
        loadDashboard(),
        loadUsers(),
        loadCourses()
      ]);
      setDashboard(dashboardData);
      setUsers(usersData);
      setCourses(coursesData);
    } catch (loadError) {
      console.error('Error al cargar dashboard admin:', loadError);
      setError(loadError.response?.data?.message || 'No se pudo cargar el dashboard administrativo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const metrics = dashboard?.metrics || {};
  const kpis = useMemo(() => ([
    { label: 'Usuarios', value: metrics.totalUsers || 0, accent: 'from-sky-500 to-cyan-500' },
    { label: 'Bloqueados', value: metrics.blockedUsers || 0, accent: 'from-rose-500 to-red-500' },
    { label: 'Cursos', value: metrics.totalCourses || 0, accent: 'from-violet-500 to-fuchsia-500' },
    { label: 'Ingresos', value: formatCurrency(metrics.revenue || 0), accent: 'from-amber-500 to-orange-500' }
  ]), [metrics]);

  const filteredUsers = useMemo(() => (
    users.filter((user) => {
      const matchesSearch = [user.name, user.email].some((value) =>
        String(value || '').toLowerCase().includes(userSearch.trim().toLowerCase())
      );
      const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter;
      const matchesStatus = userStatusFilter === 'all' || user.account_status === userStatusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    })
  ), [users, userSearch, userRoleFilter, userStatusFilter]);

  const filteredCourses = useMemo(() => (
    courses.filter((course) => {
      const haystack = [course.title, course.instructor?.name, course.instructor?.email]
        .join(' ')
        .toLowerCase();
      const matchesSearch = haystack.includes(courseSearch.trim().toLowerCase());
      const matchesStatus = courseStatusFilter === 'all' || course.status === courseStatusFilter;
      return matchesSearch && matchesStatus;
    })
  ), [courses, courseSearch, courseStatusFilter]);

  const commissionCategories = useMemo(() => {
    const categoriesMap = {};

    courses.forEach((course) => {
      const categoryKey = getCategoryKey(course);
      if (!categoriesMap[categoryKey]) {
        categoriesMap[categoryKey] = course.category || 'Sin categoria';
      }
    });

    return Object.entries(categoriesMap)
      .map(([key, label]) => ({ key, label }))
      .sort((left, right) => left.label.localeCompare(right.label, 'es'));
  }, [courses]);

  const commissionSummary = useMemo(
    () => buildCommissionSummary(courses, commissionSettings),
    [courses, commissionSettings]
  );

  const instructorRevenueRanking = useMemo(() => {
    const groupedInstructors = {};

    courses.forEach((course) => {
      const instructorId = course.instructor?.id || course.instructor_id || `sin-instructor-${course.id}`;
      const instructorName = course.instructor?.name || 'Sin instructor';
      const instructorEmail = course.instructor?.email || '';
      const courseSummary = getCommissionAmountForCourse(course, commissionSettings);

      if (!groupedInstructors[instructorId]) {
        groupedInstructors[instructorId] = {
          instructorId,
          instructorName,
          instructorEmail,
          courseCount: 0,
          completedStudents: 0,
          grossCompletedRevenue: 0,
          platformCommission: 0
        };
      }

      groupedInstructors[instructorId].courseCount += 1;
      groupedInstructors[instructorId].completedStudents += courseSummary.completedStudents;
      groupedInstructors[instructorId].grossCompletedRevenue += courseSummary.grossCompletedRevenue;
      groupedInstructors[instructorId].platformCommission += courseSummary.commissionAmount;
    });

    return Object.values(groupedInstructors)
      .sort((left, right) => right.grossCompletedRevenue - left.grossCompletedRevenue)
      .slice(0, 5);
  }, [courses, commissionSettings]);

  const topInstructorRevenue = instructorRevenueRanking[0]?.grossCompletedRevenue || 0;

  const updateCommissionRate = (categoryKey, value) => {
    const nextSettings = saveCommissionSettings({
      ...commissionSettings,
      [categoryKey]: Math.max(DEFAULT_COMMISSION_RATE, Number(value) || DEFAULT_COMMISSION_RATE)
    });
    setCommissionSettings(nextSettings);
  };

  const updateUser = async (userId, payload) => {
    const key = `user-${userId}`;
    try {
      setBusyKey(key);
      await api.put(`/users/admin/users/${userId}`, payload);
      await refreshAll();
    } catch (updateError) {
      setError(updateError.response?.data?.message || 'No se pudo actualizar el usuario');
    } finally {
      setBusyKey('');
    }
  };

  const updateCourseStatus = async (courseId, status) => {
    const key = `course-status-${courseId}`;
    try {
      setBusyKey(key);
      await api.put(`/courses/admin/${courseId}/status`, { status });
      await refreshAll();
    } catch (updateError) {
      setError(updateError.response?.data?.message || 'No se pudo actualizar el curso');
    } finally {
      setBusyKey('');
    }
  };

  const deleteCourse = async (courseId) => {
    const key = `course-delete-${courseId}`;
    try {
      const shouldDelete = window.confirm('Esta accion eliminara el curso y sus datos asociados. Deseas continuar?');
      if (!shouldDelete) {
        return;
      }
      setBusyKey(key);
      await api.delete(`/courses/admin/${courseId}`);
      await refreshAll();
    } catch (deleteError) {
      setError(deleteError.response?.data?.message || 'No se pudo eliminar el curso');
    } finally {
      setBusyKey('');
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <section className="rounded-3xl bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white p-8">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-6">
          <div>
            <p className="text-indigo-200 text-sm uppercase tracking-[0.22em] mb-3">Control total</p>
            <h1 className="text-3xl md:text-4xl font-semibold max-w-3xl">
              Panel de administracion de Educabolivia
            </h1>
            <p className="mt-3 text-slate-300 max-w-2xl">
              Supervisa usuarios, modera cursos y controla la salud operativa de la plataforma.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/10 px-5 py-4">
            <p className="text-sm text-slate-300">Sesion actual</p>
            <p className="text-lg font-semibold mt-1">{currentUser?.name || 'Administrador'}</p>
            <p className="text-sm text-slate-300">{currentUser?.email || ''}</p>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {kpis.map((item) => (
          <div key={item.label} className="rounded-3xl bg-white border border-slate-100 shadow-sm p-5">
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${item.accent} mb-4`} />
            <p className="text-sm text-slate-500">{item.label}</p>
            <p className="text-3xl font-semibold text-slate-950 mt-2">{item.value}</p>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.15fr,0.85fr] gap-6">
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
          <h2 className="text-2xl font-semibold text-slate-900 mb-5">Estado de la plataforma</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Estudiantes</p>
              <p className="text-2xl font-semibold mt-2">{metrics.totalStudents || 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Instructores</p>
              <p className="text-2xl font-semibold mt-2">{metrics.totalInstructors || 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Cursos publicados</p>
              <p className="text-2xl font-semibold mt-2">{metrics.publishedCourses || 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Borradores</p>
              <p className="text-2xl font-semibold mt-2">{metrics.draftCourses || 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Archivados</p>
              <p className="text-2xl font-semibold mt-2">{metrics.archivedCourses || 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Instructores activos</p>
              <p className="text-2xl font-semibold mt-2">{metrics.activeInstructors || 0}</p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
          <h2 className="text-2xl font-semibold text-slate-900 mb-5">Ingresos</h2>
          <div className="space-y-4">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
              <p className="text-sm text-emerald-700">Ingresos totales</p>
              <p className="text-3xl font-semibold text-emerald-900 mt-2">{formatCurrency(metrics.revenue)}</p>
            </div>
            <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
              <p className="text-sm text-amber-700">Ingresos del mes</p>
              <p className="text-3xl font-semibold text-amber-900 mt-2">{formatCurrency(metrics.monthlyRevenue)}</p>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
              <p className="text-sm text-slate-500">Inscripciones</p>
              <p className="text-2xl font-semibold text-slate-900 mt-2">{metrics.totalEnrollments || 0}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Usuarios recientes</h2>
          <div className="space-y-3">
            {(dashboard?.recentUsers || []).map((user) => (
              <div key={user.id} className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 p-4">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{user.name}</p>
                  <p className="text-sm text-slate-500 truncate">{user.email}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs uppercase tracking-wide text-slate-400">{user.role}</p>
                  <p className="text-sm text-slate-500">{formatDate(user.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Cursos recientes</h2>
          <div className="space-y-3">
            {(dashboard?.recentCourses || []).map((course) => (
              <div key={course.id} className="rounded-2xl border border-slate-100 p-4">
                <p className="font-medium text-slate-900">{course.title}</p>
                <p className="text-sm text-slate-500 mt-1">{course.instructor?.name || 'Sin instructor'}</p>
                <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
                  <span className="capitalize">{course.status}</span>
                  <span>{formatCurrency(course.price)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Pagos recientes</h2>
          <div className="space-y-3">
            {(dashboard?.recentPayments || []).slice(0, 6).map((payment) => (
              <div key={payment.id} className="rounded-2xl border border-slate-100 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-slate-900">{formatCurrency(payment.amount)}</p>
                  <p className="text-sm text-slate-500">{formatDate(payment.paid_at)}</p>
                </div>
                <p className="text-sm text-slate-500 mt-1">{payment.student?.name || 'Sin estudiante'}</p>
                <p className="text-sm text-slate-500 truncate">{payment.course?.title || 'Sin curso'}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );

  const renderUsers = () => (
    <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Gestion de usuarios</h2>
          <p className="text-sm text-slate-500">Cambia roles y bloquea cuentas con control administrativo.</p>
        </div>
        <div className="text-sm text-slate-500">{filteredUsers.length} resultados</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr,1fr,1fr] gap-3 mb-6">
        <input
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder="Buscar por nombre o email"
          className="px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
        />
        <select
          value={userRoleFilter}
          onChange={(e) => setUserRoleFilter(e.target.value)}
          className="px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
        >
          <option value="all">Todos los roles</option>
          <option value="student">Estudiantes</option>
          <option value="instructor">Instructores</option>
          <option value="admin">Admins</option>
        </select>
        <select
          value={userStatusFilter}
          onChange={(e) => setUserStatusFilter(e.target.value)}
          className="px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Activos</option>
          <option value="blocked">Bloqueados</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="pb-3 font-medium">Nombre</th>
              <th className="pb-3 font-medium">Email</th>
              <th className="pb-3 font-medium">Rol</th>
              <th className="pb-3 font-medium">Estado</th>
              <th className="pb-3 font-medium">Registro</th>
              <th className="pb-3 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className="border-b border-slate-100">
                <td className="py-4 font-medium text-slate-900">{user.name}</td>
                <td className="py-4 text-slate-600">{user.email}</td>
                <td className="py-4">
                  <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 capitalize">{user.role}</span>
                </td>
                <td className="py-4">
                  <span className={`px-3 py-1 rounded-full capitalize ${user.account_status === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {user.account_status}
                  </span>
                </td>
                <td className="py-4 text-slate-600">{formatDate(user.created_at)}</td>
                <td className="py-4">
                  <div className="flex flex-wrap gap-2">
                    {user.role !== 'admin' && (
                      <button
                        onClick={() => updateUser(user.id, { role: user.role === 'student' ? 'instructor' : 'student' })}
                        disabled={busyKey === `user-${user.id}`}
                        className="px-3 py-1 rounded-lg border border-slate-300 hover:border-slate-900 disabled:opacity-60"
                      >
                        {user.role === 'student' ? 'Hacer instructor' : 'Hacer estudiante'}
                      </button>
                    )}
                    <button
                      onClick={() => updateUser(user.id, { account_status: user.account_status === 'blocked' ? 'active' : 'blocked' })}
                      disabled={busyKey === `user-${user.id}` || user.id === currentUser?.id}
                      className={`px-3 py-1 rounded-lg disabled:opacity-60 ${user.account_status === 'blocked' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}
                    >
                      {user.account_status === 'blocked' ? 'Desbloquear' : 'Bloquear'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan="6" className="py-8 text-center text-slate-500">
                  No hay usuarios que coincidan con los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCourses = () => (
    <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Moderacion de cursos</h2>
          <p className="text-sm text-slate-500">Publica, archiva o elimina contenido desde el panel admin.</p>
        </div>
        <div className="text-sm text-slate-500">{filteredCourses.length} resultados</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr,1fr] gap-3 mb-6">
        <input
          value={courseSearch}
          onChange={(e) => setCourseSearch(e.target.value)}
          placeholder="Buscar por curso o instructor"
          className="px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
        />
        <select
          value={courseStatusFilter}
          onChange={(e) => setCourseStatusFilter(e.target.value)}
          className="px-4 py-3 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
        >
          <option value="all">Todos los estados</option>
          <option value="published">Publicados</option>
          <option value="draft">Borradores</option>
          <option value="archived">Archivados</option>
        </select>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredCourses.map((course) => (
          <div key={course.id} className="rounded-2xl border border-slate-100 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-lg font-semibold text-slate-900">{course.title}</p>
                <p className="text-sm text-slate-500 mt-1">{course.instructor?.name || 'Sin instructor'}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${course.status === 'published' ? 'bg-emerald-100 text-emerald-700' : course.status === 'archived' ? 'bg-slate-200 text-slate-700' : 'bg-amber-100 text-amber-700'}`}>
                {course.status}
              </span>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>{formatCurrency(course.price)}</span>
              <span>{formatDate(course.created_at)}</span>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {course.status !== 'published' && (
                <button
                  onClick={() => updateCourseStatus(course.id, 'published')}
                  disabled={busyKey === `course-status-${course.id}`}
                  className="px-3 py-2 rounded-lg bg-emerald-600 text-white disabled:opacity-60"
                >
                  Publicar
                </button>
              )}
              {course.status !== 'draft' && (
                <button
                  onClick={() => updateCourseStatus(course.id, 'draft')}
                  disabled={busyKey === `course-status-${course.id}`}
                  className="px-3 py-2 rounded-lg border border-slate-300 hover:border-slate-900 disabled:opacity-60"
                >
                  Pasar a borrador
                </button>
              )}
              {course.status !== 'archived' && (
                <button
                  onClick={() => updateCourseStatus(course.id, 'archived')}
                  disabled={busyKey === `course-status-${course.id}`}
                  className="px-3 py-2 rounded-lg bg-slate-800 text-white disabled:opacity-60"
                >
                  Archivar
                </button>
              )}
              <button
                onClick={() => deleteCourse(course.id)}
                disabled={busyKey === `course-delete-${course.id}`}
                className="px-3 py-2 rounded-lg bg-red-600 text-white disabled:opacity-60"
              >
                Eliminar
              </button>
            </div>
          </div>
        ))}
        {filteredCourses.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500 lg:col-span-2">
            No hay cursos que coincidan con los filtros actuales.
          </div>
        )}
      </div>
    </div>
  );

  const renderFinance = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-[0.95fr,1.05fr] gap-6">
        <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Comisiones por rubro</h2>
              <p className="text-sm text-slate-500">Define el porcentaje con minimo del 20% por categoria.</p>
            </div>
          </div>
          <div className="space-y-4">
            {commissionCategories.map((category) => (
              <div key={category.key} className="rounded-2xl border border-slate-100 p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="font-medium text-slate-900">{category.label}</p>
                    <p className="text-sm text-slate-500">Se aplica solo cuando el estudiante finaliza el curso.</p>
                  </div>
                  <div className="flex items-center gap-3 md:min-w-[220px]">
                    <input
                      type="range"
                      min={DEFAULT_COMMISSION_RATE}
                      max="95"
                      step="1"
                      value={commissionSettings[category.key] || DEFAULT_COMMISSION_RATE}
                      onChange={(e) => updateCommissionRate(category.key, e.target.value)}
                      className="w-full accent-slate-900"
                    />
                    <input
                      type="number"
                      min={DEFAULT_COMMISSION_RATE}
                      max="95"
                      value={commissionSettings[category.key] || DEFAULT_COMMISSION_RATE}
                      onChange={(e) => updateCommissionRate(category.key, e.target.value)}
                      className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-right"
                    />
                    <span className="text-sm text-slate-500">%</span>
                  </div>
                </div>
              </div>
            ))}
            {commissionCategories.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
                Aun no hay categorias con cursos cargados.
              </div>
            )}
          </div>
        </section>

        <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Resumen de comisiones</h2>
              <p className="text-sm text-slate-500">Calculado sobre cursos finalizados y agrupado por tipo de curso.</p>
            </div>
          </div>
          <div className="space-y-3">
            {commissionSummary.map((item) => (
              <div key={item.categoryKey} className="rounded-2xl border border-slate-100 p-4">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">{item.categoryLabel}</p>
                    <p className="text-sm text-slate-500">
                      {item.courseCount} curso{item.courseCount === 1 ? '' : 's'} · {item.completedStudents} finalizados
                    </p>
                  </div>
                  <div className="text-left lg:text-right">
                    <p className="text-sm text-slate-500">Comision aplicada</p>
                    <p className="text-lg font-semibold text-slate-900">{item.commissionRate}%</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  <div className="rounded-2xl bg-slate-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Estudiantes estimados</p>
                    <p className="text-lg font-semibold text-slate-900 mt-1">{item.estimatedStudents}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Base finalizada</p>
                    <p className="text-lg font-semibold text-slate-900 mt-1">{formatCurrency(item.grossCompletedRevenue)}</p>
                  </div>
                  <div className="rounded-2xl bg-emerald-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-emerald-600">Comision total</p>
                    <p className="text-lg font-semibold text-emerald-900 mt-1">{formatCurrency(item.commissionAmount)}</p>
                  </div>
                </div>
              </div>
            ))}
            {commissionSummary.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
                No hay cursos disponibles para calcular comisiones.
              </div>
            )}
          </div>
        </section>
      </div>

      <section className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Instructores que mas ingresos generan</h2>
            <p className="text-sm text-slate-500">
              Ranking segun ingresos de cursos finalizados. Tambien se muestra la comision que recibe la plataforma.
            </p>
          </div>
        </div>

        {instructorRevenueRanking.length > 0 && (
          <div className="rounded-[28px] bg-[#0e1020] text-white p-6 mb-6 overflow-hidden">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <p className="text-2xl font-semibold">Top 5 instructores por ingresos</p>
                <p className="text-sm text-violet-200 mt-1">Ingresos generados para la plataforma</p>
              </div>
              <div className="text-sm text-violet-200">Base: cursos finalizados</div>
            </div>

            <div className="flex items-end justify-between gap-3 md:gap-5 min-h-[260px] border-l border-b border-white/10 pl-4 pb-4">
              {instructorRevenueRanking.map((item, index) => {
                const barHeight = topInstructorRevenue > 0
                  ? Math.max(18, Math.round((item.grossCompletedRevenue / topInstructorRevenue) * 100))
                  : 0;
                const barColors = [
                  'from-violet-400 to-violet-600',
                  'from-sky-400 to-blue-500',
                  'from-pink-400 to-rose-500',
                  'from-emerald-400 to-green-500',
                  'from-amber-400 to-orange-500'
                ];
                const shortName = item.instructorName
                  .split(' ')
                  .slice(0, 2)
                  .join(' ');

                return (
                  <div key={item.instructorId} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-3">
                    <p className="text-xs md:text-sm text-violet-100 font-medium">
                      {formatCurrency(item.grossCompletedRevenue)}
                    </p>
                    <div className="relative w-full flex justify-center items-end h-[180px]">
                      <div
                        className={`w-full max-w-[88px] rounded-t-2xl bg-gradient-to-b ${barColors[index % barColors.length]} shadow-[0_0_30px_rgba(139,92,246,0.25)]`}
                        style={{ height: `${barHeight}%` }}
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-sm md:text-base font-semibold text-white leading-tight">{shortName}</p>
                      <p className="text-xs text-violet-200 mt-1">
                        {item.completedStudents} finalizados
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-4">
          {instructorRevenueRanking.map((item, index) => {
            const width = topInstructorRevenue > 0
              ? Math.max(12, Math.round((item.grossCompletedRevenue / topInstructorRevenue) * 100))
              : 0;

            return (
              <div key={item.instructorId} className="rounded-2xl border border-slate-100 p-4">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-slate-950 text-white text-sm font-semibold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-semibold text-slate-900">{item.instructorName}</p>
                        <p className="text-sm text-slate-500">{item.instructorEmail || 'Instructor sin email visible'}</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 mt-3">
                      {item.courseCount} curso{item.courseCount === 1 ? '' : 's'} · {item.completedStudents} estudiantes finalizados
                    </p>
                  </div>
                  <div className="text-left lg:text-right">
                    <p className="text-sm text-slate-500">Ingreso generado</p>
                    <p className="text-xl font-semibold text-slate-900">{formatCurrency(item.grossCompletedRevenue)}</p>
                    <p className="text-sm text-emerald-700 mt-1">Comision plataforma: {formatCurrency(item.platformCommission)}</p>
                  </div>
                </div>

                <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-emerald-500"
                    style={{ width: `${width}%` }}
                  />
                </div>
              </div>
            );
          })}

          {instructorRevenueRanking.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
              No hay instructores con ingresos calculables todavia.
            </div>
          )}
        </div>
      </section>

      <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Pagos recientes</h2>
            <p className="text-sm text-slate-500">Ultimos movimientos completados registrados.</p>
          </div>
        </div>
        <div className="space-y-3">
          {(dashboard?.recentPayments || []).map((payment, index) => (
            <div key={`${payment.payment_method}-${payment.paid_at}-${index}`} className="rounded-2xl border border-slate-100 px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <p className="font-medium text-slate-900">{formatCurrency(payment.amount)}</p>
                <p className="text-sm text-slate-500">{payment.payment_method}</p>
                <p className="text-sm text-slate-500">{payment.student?.name || 'Sin estudiante'}</p>
                <p className="text-sm text-slate-500">{payment.course?.title || 'Sin curso'}</p>
              </div>
              <div className="text-sm text-slate-500 md:text-right">
                <p>{formatDate(payment.paid_at)}</p>
                <p className="truncate">{payment.payment_reference}</p>
              </div>
            </div>
          ))}
          {(dashboard?.recentPayments || []).length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
              Aun no hay pagos completados registrados.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return renderUsers();
      case 'courses':
        return renderCourses();
      case 'finance':
        return renderFinance();
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-600">Cargando dashboard administrativo...</div>;
  }

  return (
    <div className="min-h-screen bg-[#f5f7fb]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 rounded-2xl bg-red-50 border border-red-100 text-red-700 p-4">
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 xl:grid-cols-[280px,1fr] gap-6">
          <aside className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 h-fit xl:sticky xl:top-24">
            <div className="pb-6 border-b border-slate-100">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Administrador</p>
              <p className="text-xl font-semibold text-slate-900">{currentUser?.name || 'Admin'}</p>
              <p className="text-sm text-slate-500 mt-1">{currentUser?.email || ''}</p>
            </div>

            <nav className="mt-6 space-y-2">
              {adminTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left rounded-2xl px-4 py-3 transition-colors ${
                    activeTab === tab.id ? 'bg-slate-950 text-white' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>

            <div className="mt-6 pt-6 border-t border-slate-100 space-y-3">
              <button
                onClick={refreshAll}
                className="w-full text-left rounded-2xl px-4 py-3 text-slate-600 hover:bg-slate-50"
              >
                Actualizar datos
              </button>
              <button
                onClick={() => onNavigate?.('home')}
                className="w-full text-left rounded-2xl px-4 py-3 text-slate-600 hover:bg-slate-50"
              >
                Ir al inicio
              </button>
              <button
                onClick={onLogout}
                className="w-full text-left rounded-2xl px-4 py-3 text-red-600 hover:bg-red-50"
              >
                Cerrar sesion
              </button>
            </div>
          </aside>

          <main>{renderContent()}</main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
